import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="agent-form"
export default class extends Controller {
  static targets = ["publicWelcomeMessage", "chatAccessSelect", "shareLinkCodeField", "shareLinkModeSelect", "listManagementOptions", "listManagementToggle", "boardCheckbox", "boardWarning", "retentionModeSelect", "retentionSettings", "codeInterpreterToggle", "codeInterpreterSettings", "completionApprovalToggle", "completionApprovalSettings"]

  connect() {
    this.togglePublicWelcomeMessage()
    this.toggleListManagementOptions()
    this.toggleBoardWarning()
    this.toggleRetentionSettings()
    this.toggleCompletionApprovalSettings()
  }

  // A required field on a hidden tab or inside a closed Advanced row blocks Save
  // with no visible message. Show its tab and open its row so the browser can
  // point at it. Bound with :capture because `invalid` does not bubble.
  revealInvalid(event) {
    const field = event.target
    const panel = field.closest('[data-tabs-target="panel"]')
    if (panel && panel.classList.contains("hidden")) {
      this.element.querySelector(`[data-tabs-target="tab"][data-tab="${panel.dataset.tab}"]`)?.click()
    }
    const details = field.closest("details")
    if (details && !details.open) details.open = true
  }

  // Called when chat access dropdown changes
  onChatAccessChange() {
    this.togglePublicWelcomeMessage()
  }

  togglePublicWelcomeMessage() {
    if (!this.hasChatAccessSelectTarget || !this.hasPublicWelcomeMessageTarget) {
      return
    }

    const isPublic = this.chatAccessSelectTarget.value === 'public_customer_service'
    this.publicWelcomeMessageTarget.classList.toggle('hidden', !isPublic)
  }

  // Called when share link access mode select changes
  onShareLinkModeChange() {
    if (!this.hasShareLinkCodeFieldTarget || !this.hasShareLinkModeSelectTarget) return

    this.shareLinkCodeFieldTarget.classList.toggle('hidden', this.shareLinkModeSelectTarget.value !== 'pin')
  }

  onListManagementChange() {
    this.toggleListManagementOptions()
  }

  toggleListManagementOptions() {
    if (!this.hasListManagementOptionsTarget || !this.hasListManagementToggleTarget) return

    this.listManagementOptionsTarget.classList.toggle('hidden', !this.listManagementToggleTarget.checked)
  }

  onBoardSelectionChange() {
    this.toggleBoardWarning()
  }

  // Hide the "no boards allowed yet" warning as soon as any board is selected.
  toggleBoardWarning() {
    if (!this.hasBoardWarningTarget) return

    const anySelected = this.boardCheckboxTargets.some(checkbox => checkbox.checked)
    this.boardWarningTarget.classList.toggle('hidden', anySelected)
  }

  onRetentionModeChange() {
    this.toggleRetentionSettings()
  }

  onCodeInterpreterChange() {
    this.toggleCodeInterpreterSettings()
  }

  // Keep the higher-risk network option tucked under the capability it changes.
  // Turning Code Interpreter off also clears package installs: leaving it
  // checked under a disabled tool reads as if it were still in effect, and the
  // model-level validation would reject the save anyway.
  toggleCodeInterpreterSettings() {
    if (!this.hasCodeInterpreterToggleTarget || !this.hasCodeInterpreterSettingsTarget) return

    const enabled = this.codeInterpreterToggleTarget.checked
    this.codeInterpreterSettingsTarget.classList.toggle('hidden', !enabled)
    if (!enabled) {
      this.codeInterpreterSettingsTarget
        .querySelectorAll("input[type=checkbox]")
        .forEach(input => { input.checked = false })
    }
  }

  onCompletionApprovalChange() {
    this.toggleCompletionApprovalSettings()
  }

  // Who reviews and how many only apply once approval is required, so they stay
  // under the switch. Unlike the Code Interpreter pair these are not a further
  // permission, so turning approval off leaves the reviewer choice intact — it
  // is the setting to return to if approval is switched back on.
  toggleCompletionApprovalSettings() {
    if (!this.hasCompletionApprovalToggleTarget || !this.hasCompletionApprovalSettingsTarget) return

    this.completionApprovalSettingsTarget.classList.toggle('hidden', !this.completionApprovalToggleTarget.checked)
  }

  toggleRetentionSettings() {
    if (!this.hasRetentionModeSelectTarget || !this.hasRetentionSettingsTarget) return

    const enabled = this.retentionModeSelectTarget.value === 'enabled'
    this.retentionSettingsTarget.classList.toggle('hidden', !enabled)
    this.retentionSettingsTarget.querySelectorAll("input, select, textarea").forEach(input => {
      if (enabled) {
        if (input.dataset.wasRequired) input.required = true
      } else {
        if (input.required) input.dataset.wasRequired = "true"
        input.required = false
        if (input.type === "checkbox" || input.type === "radio") {
          input.checked = false
        } else {
          input.value = ""
        }
      }
    })
  }
}
